import React from 'react';
import {
    TextField,
    Autocomplete,
    TextFieldVariants,
    PopperProps,
    styled,
    createFilterOptions,
    FilterOptionsState,
    AutocompleteRenderInputParams
} from '@mui/material';
import { Option } from 'lib/types';

export interface CreatableChipInputProps {
    label?: React.ReactNode;
    placeholder?: string;
    options?: Option[];
    description?: React.ReactNode;
    disabled?: boolean;
    className?: string;
    emptyLabel?: string;
    disableClearable?: boolean;
    onSelect?: (value: string[]) => void;
    readOnly?: boolean;
    textFieldVariant?: TextFieldVariants;
    size?: 'small' | 'medium';
    popperProps?: Partial<PopperProps>;
    limitTags?: number;
    value?: string[];
    onChange?: (value: string[]) => void;
    error?: boolean;
    helperText?: string;
    name?: string;
    maxInputHeight?: number;
}

const StyledAutocomplete = styled(Autocomplete)({
    '& .MuiAutocomplete-tag': {
        maxWidth: 150
    }
});

const filter = createFilterOptions<Option>();

export const CreatableChipInput: React.FC<CreatableChipInputProps> = ({
    label,
    options = [],
    disabled,
    limitTags,
    popperProps,
    placeholder,
    onSelect,
    value = [],
    onChange,
    error,
    name,
    emptyLabel,
    disableClearable,
    textFieldVariant,
    readOnly,
    maxInputHeight
}) => {
    const [inputValue, setInputValue] = React.useState('');

    const selectedValues = React.useMemo(() => {
        if (Array.isArray(value)) {
            return value;
        }
        return value ? [value] : [];
    }, [value]);

    const selectedOptions = React.useMemo(
        () =>
            selectedValues.map(value => {
                const existingOption = options.find(option => option.value === value);
                if (existingOption) {
                    return existingOption;
                }
                return { label: value, value };
            }),
        [selectedValues, options]
    );

    const handleChange = React.useCallback(
        (_: never, newValue: (Option | string)[]) => {
            const stringValues = newValue.map(item => {
                if (typeof item === 'string') {
                    return item;
                }
                return item.value;
            });

            const formattedValues: string[] = [];
            for (const value of stringValues) {
                if (value.includes(',')) {
                    const splitValues = value.split(',').map(item => item.trim());
                    const uniqueValues = splitValues.filter(item => !formattedValues.includes(item));
                    formattedValues.push(...uniqueValues);
                } else {
                    formattedValues.push(value);
                }
            }

            onChange?.(formattedValues);
            onSelect?.(formattedValues);
        },
        [onChange, onSelect]
    );

    const handleInputChange = React.useCallback((_: never, newInputValue: string) => {
        setInputValue(newInputValue);
    }, []);

    const handleInputKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            const trimmedInput = inputValue.trim();

            if (!trimmedInput) {
                return;
            }

            if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',') {
                event.preventDefault();

                const valueExists = selectedValues.some(value => value === trimmedInput);
                if (valueExists) {
                    setInputValue('');
                    return;
                }
                const newValues = [...selectedValues];

                if (trimmedInput.includes(',')) {
                    const splitValues = trimmedInput.split(',').map(item => item.trim());
                    const uniqueValues = splitValues.filter(item => !selectedValues.includes(item));
                    newValues.push(...uniqueValues);
                } else {
                    newValues.push(trimmedInput);
                }

                onChange?.(newValues);
                onSelect?.(newValues);
                setInputValue('');
            }
        },
        [inputValue, selectedValues, onChange, onSelect]
    );

    const handleComponentKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement> & { defaultMuiPrevented?: boolean }) => {
            event.defaultMuiPrevented = true;
        },
        []
    );

    const filterOptions = React.useCallback(
        (options: Option[], state: FilterOptionsState<Option>) => {
            const filtered = filter(options, state);

            const { inputValue } = state;
            const inputExists = selectedValues.some(value => value === inputValue);
            if (inputExists) {
                return filtered;
            }

            const hasCommas = inputValue.includes(',');
            const formattedValues = hasCommas
                ? inputValue
                      .split(',')
                      .map(item => item.trim())
                      .filter(item => item !== '')
                      .map((item, index, allValues) => {
                          if (index === allValues.length - 1) {
                              return `and "${item}"`;
                          }
                          return `"${item}"`;
                      })
                      .join(', ')
                : `"${inputValue}"`;
            if (inputValue !== '') {
                filtered.push({
                    label: `Add ${formattedValues}`,
                    value: inputValue
                });
            }

            return filtered;
        },
        [selectedValues]
    );

    const placeholderText = React.useMemo(() => {
        const text = placeholder ?? 'Add items...';
        if (selectedOptions.length > 0) {
            return undefined;
        }
        return text;
    }, [selectedOptions, placeholder]);

    const renderInput = React.useCallback(
        (params: AutocompleteRenderInputParams) => {
            const { InputProps, ...restParams } = params;
            const { startAdornment, ...restInputProps } = InputProps;
            const isModified = maxInputHeight && maxInputHeight > 0;
            return (
                <TextField
                    {...restParams}
                    InputProps={{
                        ...restInputProps,
                        startAdornment: isModified ? (
                            <div style={{ maxHeight: maxInputHeight, overflowY: 'auto' }}>
                                {startAdornment}
                            </div>
                        ) : (
                            startAdornment
                        )
                    }}
                    variant={textFieldVariant}
                    error={error}
                    label={label}
                    fullWidth
                    placeholder={placeholderText}
                    onKeyDown={handleInputKeyDown}
                    name={name}
                />
            );
        },
        [error, label, textFieldVariant, placeholderText, handleInputKeyDown, name, maxInputHeight]
    );

    const isOptionEqualToValue = React.useCallback(
        (option: Option, value: Option) => option.value === value.value,
        []
    );

    const getOptionsLabel = React.useCallback(item => (typeof item.label === 'string' ? item.label : ''), []);

    return (
        <StyledAutocomplete
            value={selectedOptions}
            options={options}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            onKeyDown={handleComponentKeyDown}
            onChange={handleChange}
            onInputChange={handleInputChange}
            filterOptions={filterOptions}
            inputValue={inputValue}
            renderInput={renderInput}
            limitTags={limitTags}
            multiple={true}
            disabled={disabled}
            disableCloseOnSelect={true}
            getOptionLabel={getOptionsLabel}
            fullWidth
            freeSolo={true}
            noOptionsText={emptyLabel}
            disableClearable={disableClearable}
            isOptionEqualToValue={isOptionEqualToValue}
            readOnly={readOnly}
            slotProps={{
                popper: {
                    ...popperProps
                }
            }}
        />
    );
};
